import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Euro, TrendingUp, HardHat, Users, Calendar, ArrowDownRight, ArrowUpRight, Receipt, Hammer, Wallet, Pencil, Check, AlertTriangle, Clock, MessageCircle, Sparkles, Loader2, RefreshCw, LogIn, LogOut as LogOutIcon, Fingerprint, MapPin, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import QuickActions from "@/components/QuickActions";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [promemoria, setPromemoria] = useState([]);
  const [anomalie, setAnomalie] = useState(null); // {items, generated_at, signals}
  const [anomBusy, setAnomBusy] = useState(false);
  const [timbrature, setTimbrature] = useState([]); // Globali di tutto il team
  const [prossimiApp, setProssimiApp] = useState([]); // Prossimi 3 appuntamenti
  const [realizzato, setRealizzato] = useState(null); // { data, totale, voci: [...], multi_cantiere }

  const reload = () => {
    api.get("/dashboard/admin").then(({ data }) => setD(data));
    api.get("/promemoria-fatture?giorni=7").then(({ data }) => setPromemoria(data)).catch(() => {});
    api.get("/ai/anomalie").then(({ data }) => setAnomalie(data)).catch(() => {});
    api.get("/timbrature").then(({ data }) => setTimbrature((data || []).slice(0, 15))).catch(() => {});
    api.get("/dashboard/realizzato-oggi").then(({ data }) => setRealizzato(data)).catch(() => setRealizzato({ totale: 0, voci: [], multi_cantiere: false }));
    api.get("/appuntamenti").then(({ data }) => {
      const now = new Date();
      setProssimiApp((data || []).filter((a) => new Date(a.data_ora) >= now).sort((a, b) => new Date(a.data_ora) - new Date(b.data_ora)).slice(0, 3));
    }).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  const scanAnomalie = async () => {
    setAnomBusy(true);
    try {
      const { data } = await api.get("/ai/anomalie?force=true", { timeout: 60000 });
      setAnomalie(data);
      toast.success(`${(data?.items || []).length} anomalie rilevate`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore scansione");
    } finally { setAnomBusy(false); }
  };

  const markSent = async (fid) => {
    try {
      await api.post("/promemoria-fatture/mark-sent", { fattura_id: fid, canale: "whatsapp" });
      toast.success("Promemoria segnato come inviato");
      reload();
    } catch { toast.error("Errore"); }
  };

  if (!d) return <div className="text-muted-foreground">Caricamento dashboard...</div>;

  const kpis = [
    { label: "Cantieri attivi", value: d.cantieri_totali, icon: HardHat, color: "text-primary" },
    { label: "Operai in squadra", value: d.operai_totali, icon: Users, color: "text-accent" },
    { label: "Presenze oggi", value: d.presenze_oggi, icon: Calendar, color: "text-emerald-600" },
    { label: "IVA saldo", value: fmt(d.iva_saldo), icon: Receipt, color: d.iva_saldo >= 0 ? "text-emerald-600" : "text-red-600" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Pannello Amministratore</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Panoramica aziendale</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} data-testid={`kpi-${k.label}`} className="card-elevated">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
                  <div className="text-2xl md:text-3xl font-extrabold mt-1 tabular-nums">{k.value}</div>
                </div>
                <k.icon className={`h-5 w-5 ${k.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <QuickActions />

      {/* Widget "Realizzato Oggi" — produzione di giornata su cantieri attivi */}
      <Card className="card-elevated" data-testid="widget-realizzato-oggi">
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Produzione di Giornata</div>
              <div className="text-xl font-bold flex items-center gap-2">
                Realizzato Oggi
                <span className="text-xs font-normal text-muted-foreground">
                  {realizzato?.data
                    ? new Date(realizzato.data).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })
                    : new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-4xl md:text-5xl font-extrabold text-primary tabular-nums" data-testid="realizzato-oggi-totale">
              {fmt(realizzato?.totale || 0)}
            </div>
          </div>
          {!realizzato || (realizzato.voci || []).length === 0 ? (
            <div className="mt-4 text-center text-sm text-red-600 font-medium" data-testid="realizzato-oggi-empty">
              😔 Nessuna voce rendicontata oggi
            </div>
          ) : (
            <ul className="mt-4 space-y-1.5" data-testid="realizzato-oggi-lista">
              {realizzato.voci.map((v, i) => (
                <li key={`${v.voce_id}-${i}`} className="flex items-center justify-between text-sm gap-3 py-1 border-b border-border/40 last:border-0">
                  <span className="truncate flex-1">
                    {realizzato.multi_cantiere && (
                      <span className="text-xs text-muted-foreground mr-1.5">[{v.cantiere_nome}]</span>
                    )}
                    • {v.descrizione}
                    <span className="text-muted-foreground ml-2">— {v.percentuale}%</span>
                  </span>
                  <span className="font-mono font-semibold tabular-nums text-primary whitespace-nowrap">{fmt(v.valore)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {promemoria.length > 0 && (
        <Card className="card-elevated border-emerald-600" data-testid="promemoria-wa">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="h-5 w-5 text-emerald-600" />
              <div className="font-bold">{promemoria.length} promemoria WhatsApp da inviare (fatture in scadenza entro 7gg)</div>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {promemoria.slice(0, 6).map((p) => (
                <div key={p.fattura_id} className="flex items-center justify-between gap-3 p-2 rounded-md text-sm bg-emerald-50 dark:bg-emerald-900/20">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.controparte}</div>
                    <div className="text-xs text-muted-foreground">{p.cantiere_nome} · scade {p.data_scadenza} ({p.giorni}gg)</div>
                    {p.ultimo_invio && <div className="text-[10px] text-emerald-700">Ultimo invio: {new Date(p.ultimo_invio).toLocaleDateString("it-IT")}</div>}
                  </div>
                  <div className="font-bold tabular-nums text-sm">{fmt(p.totale)}</div>
                  {p.whatsapp_link ? (
                    <a href={p.whatsapp_link} target="_blank" rel="noreferrer" onClick={() => markSent(p.fattura_id)} data-testid={`wa-${p.fattura_id}`}>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8">
                        <MessageCircle className="h-3 w-3 mr-1" /> Invia
                      </Button>
                    </a>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">manca tel.</span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {anomalie && (anomalie.fatture_scadute?.length > 0) && (
        <Card className="card-elevated border-destructive/70" data-testid="scadute-azioni-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <div className="font-bold">
                {anomalie.fatture_scadute.length} fattur{anomalie.fatture_scadute.length === 1 ? "a" : "e"} scadut{anomalie.fatture_scadute.length === 1 ? "a" : "e"} · azioni rapide
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {anomalie.fatture_scadute.slice(0, 8).map((f) => {
                const isVendita = f.tipo === "vendita";
                return (
                  <div
                    key={f.fattura_id}
                    data-testid={`scaduta-${f.fattura_id}`}
                    className={`rounded-lg border p-3 ${isVendita ? "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10" : "border-amber-300 bg-amber-50/40 dark:bg-amber-900/10"}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-bold">
                          {isVendita
                            ? <span className="text-emerald-700 dark:text-emerald-300">Credito · Cliente</span>
                            : <span className="text-amber-700 dark:text-amber-300">Debito · Fornitore</span>}
                        </div>
                        <div className="font-bold truncate">{f.controparte}</div>
                        <div className="text-xs text-muted-foreground">
                          Scadenza {f.data_scadenza} · <span className="font-semibold text-destructive">{f.giorni_ritardo}gg di ritardo</span>
                        </div>
                      </div>
                      <div className="text-right font-bold tabular-nums whitespace-nowrap">{fmt(f.totale)}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        data-testid={`dash-aggiorna-${f.fattura_id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => window.location.href = "/fatture"}
                      >
                        <Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Aggiorna pagamento
                      </Button>
                      {isVendita && (
                        <Button
                          data-testid={`dash-sollecito-${f.fattura_id}`}
                          size="sm"
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/fatture/${f.fattura_id}/sollecito-email`);
                              toast.success(data?.used_fallback
                                ? `Sollecito inviato a ${data.recipient} (fallback admin, cliente senza email)`
                                : `Sollecito inviato a ${data.recipient}`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Errore invio sollecito");
                            }
                          }}
                        >
                          <MessageCircle className="h-3.5 w-3.5 mr-1" /> Invia promemoria
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {anomalie && (
        <Card className={`card-elevated ${(anomalie.items || []).some((i) => i.severita === "alta") ? "border-destructive" : "border-amber-500"}`} data-testid="ai-anomalie-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <div className="font-bold">
                  Anomalie AI rilevate
                  {anomalie.items?.length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">{anomalie.items.length}</Badge>
                  )}
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {anomalie.generated_at && `agg: ${new Date(anomalie.generated_at).toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}`}
                </span>
              </div>
              <Button data-testid="scan-anomalie" variant="outline" size="sm" disabled={anomBusy} onClick={scanAnomalie}>
                {anomBusy ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-2" />}
                Scansiona di nuovo
              </Button>
            </div>
            {anomalie.items?.length === 0 ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-600" /> Nessuna anomalia rilevata negli ultimi 30 giorni.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-2">
                {(anomalie.items || []).map((it, i) => {
                  const sev = it.severita || "media";
                  const bg = sev === "alta" ? "bg-destructive/10 border-destructive/30" : sev === "media" ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300" : "bg-secondary";
                  const dot = sev === "alta" ? "bg-destructive" : sev === "media" ? "bg-amber-500" : "bg-muted-foreground";
                  return (
                    <div key={i} data-testid={`anomalia-${i}`} className={`p-3 rounded-md text-sm border ${bg}`}>
                      <div className="flex items-start gap-2">
                        <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${dot}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-bold">{it.titolo}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{it.descrizione}</div>
                          {it.consiglio && (
                            <div className="text-xs mt-1.5 flex items-start gap-1">
                              <Sparkles className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                              <span className="text-foreground">{it.consiglio}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {(d.scadute?.length > 0 || d.scadenze_imminenti?.length > 0) && (
        <Card className={`card-elevated ${d.scadute?.length > 0 ? "border-destructive" : "border-amber-500"}`} data-testid="scadenze-alert">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className={`h-5 w-5 ${d.scadute?.length > 0 ? "text-destructive" : "text-amber-600"}`} />
              <div className="font-bold">
                {d.scadute?.length > 0 && <span className="text-destructive">{d.scadute.length} scadute · </span>}
                {d.scadenze_imminenti?.length > 0 && <span className="text-amber-700 dark:text-amber-500">{d.scadenze_imminenti.length} in scadenza (7 giorni)</span>}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {[...(d.scadute || []), ...(d.scadenze_imminenti || [])].slice(0, 6).map((s) => {
                const scaduta = s.giorni < 0;
                return (
                  <div key={s.id} className={`flex items-center justify-between gap-3 p-2 rounded-md text-sm ${scaduta ? "bg-destructive/10" : "bg-amber-50 dark:bg-amber-900/20"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{s.controparte}</div>
                      <div className="text-xs text-muted-foreground">{s.data_scadenza} · {s.tipo}</div>
                    </div>
                    <div className={`text-xs font-bold ${scaduta ? "text-destructive" : "text-amber-700 dark:text-amber-500"}`}>
                      {scaduta ? `Scaduta ${-s.giorni}gg` : s.giorni === 0 ? "Oggi" : `${s.giorni}gg`}
                    </div>
                    <div className="font-bold tabular-nums">{fmt(s.totale)}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="card-elevated lg:col-span-3">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Flussi Finanziari</div>
              <div className="text-xl font-bold">Fatture & IVA</div>
            </div>
            <Row icon={ArrowUpRight} label="Fatture vendita" value={fmt(d.tot_vendite)} tone="pos" />
            <Row icon={ArrowDownRight} label="Fatture acquisto" value={fmt(d.tot_acquisti)} tone="neg" />
            <div className="h-px bg-border" />
            <Row icon={TrendingUp} label="IVA a debito" value={fmt(d.iva_debito)} />
            <Row icon={TrendingUp} label="IVA a credito" value={fmt(d.iva_credito)} />
            <div className="h-px bg-border" />
            <Row icon={Euro} label="Da pagare" value={fmt(d.da_pagare)} tone="neg" />
            <Row icon={Euro} label="Da incassare" value={fmt(d.da_incassare)} tone="pos" />
          </CardContent>
        </Card>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-6">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cantieri</div>
          <div className="text-xl font-bold mb-4">Avanzamento lavori & pagamenti</div>
          <div className="space-y-6">
            {d.cantieri.map((c) => (
              <CantiereRow key={c.id} c={c} onSaved={reload} />
            ))}
            {d.cantieri.length === 0 && <div className="text-sm text-muted-foreground">Nessun cantiere.</div>}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6" data-testid="dash-bottom-grid">
        <Card className="card-elevated lg:col-span-2" data-testid="timbrature-recenti-team">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Timbrature recenti</div>
                <div className="text-xl font-bold">Attività del team</div>
              </div>
              <Badge variant="secondary" className="text-xs">{timbrature.length} eventi</Badge>
            </div>
            {timbrature.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6">Nessuna timbratura recente del team.</div>
            ) : (
              <div className="space-y-1">
                {timbrature.map((t) => {
                  // Nome di battesimo: prima parola di operaio_nome
                  const firstName = (t.operaio_nome || "").trim().split(/\s+/)[0] || "—";
                  const isEntrata = t.tipo === "entrata";
                  return (
                    <div
                      key={t.id}
                      data-testid={`timbr-team-${t.id}`}
                      className="flex items-center justify-between gap-3 text-sm py-2 border-b border-border last:border-0"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {isEntrata
                          ? <LogIn className="h-4 w-4 text-emerald-600 shrink-0" />
                          : <LogOutIcon className="h-4 w-4 text-red-600 shrink-0" />}
                        <div className="min-w-0">
                          <div className="font-semibold truncate">
                            {isEntrata ? "Entrata" : "Uscita"}
                            <span className="font-normal text-muted-foreground"> · </span>
                            <span className="text-primary">{firstName}</span>
                            {t.biometric_verified && <Fingerprint className="inline h-3 w-3 ml-1 text-emerald-600" />}
                            {t.anomalia && !t.anomalia.risolta && <AlertTriangle className="inline h-3 w-3 ml-1 text-destructive" />}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {t.data} · {t.ora}
                            {t.corretta_da_admin && <span className="ml-2 text-amber-600">corretta</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card
          className="card-elevated border-primary/30 hover:border-primary/60 transition-colors cursor-pointer"
          data-testid="dash-prossimi-appuntamenti"
          onClick={() => navigate("/appuntamenti")}
          title="Vai all'agenda"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Agenda</div>
                <div className="text-xl font-bold">Prossimi appuntamenti</div>
              </div>
              <Badge variant="outline" className="text-xs border-primary/40 text-primary">
                {prossimiApp.length} <ArrowRight className="h-3 w-3 ml-1" />
              </Badge>
            </div>
            {prossimiApp.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6">
                <Calendar className="h-8 w-8 mx-auto mb-2 opacity-40" />
                Nessun appuntamento in programma.
              </div>
            ) : (
              <div className="space-y-3">
                {prossimiApp.map((a) => {
                  const dt = new Date(a.data_ora);
                  const now = new Date();
                  const isToday = dt.toDateString() === now.toDateString();
                  const diffH = Math.round((dt - now) / (1000 * 60 * 60));
                  const relText = isToday
                    ? (diffH <= 1 ? "Tra meno di 1h" : `Tra ${diffH}h`)
                    : (diffH < 48 ? "Domani" : dt.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }));
                  return (
                    <div
                      key={a.id}
                      data-testid={`dash-app-${a.id}`}
                      className="flex items-start gap-3 pb-3 border-b border-border last:border-0 last:pb-0"
                    >
                      <div className={`shrink-0 w-11 h-11 rounded-xl flex flex-col items-center justify-center ${isToday ? "bg-primary/15 text-primary border border-primary/30" : "bg-secondary text-muted-foreground"}`}>
                        <div className="text-[9px] uppercase font-bold leading-none">{dt.toLocaleDateString("it-IT", { month: "short" }).replace(".", "")}</div>
                        <div className="text-base font-extrabold leading-none mt-0.5">{dt.getDate()}</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{a.titolo}</div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Clock className="h-3 w-3" />
                          {dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {relText}
                        </div>
                        {a.luogo && <div className="text-[11px] text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{a.luogo}</span></div>}
                      </div>
                    </div>
                  );
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-primary hover:bg-primary/10 mt-2"
                  onClick={(e) => { e.stopPropagation(); navigate("/appuntamenti"); }}
                  data-testid="dash-apri-agenda"
                >
                  Apri agenda completa <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, tone }) {
  const color = tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-red-600" : "text-foreground";
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className={`h-4 w-4 ${color}`} /> {label}
      </div>
      <div className={`font-bold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

function CantiereRow({ c, onSaved }) {
  const pctLavori = Math.max(0, Math.min(100, Number(c.percentuale_avanzamento || 0)));
  const pctPag = Math.max(0, Math.min(100, Number(c.percentuale_pagamenti || 0)));
  const pctBudget = c.budget > 0 ? Math.min(100, (c.costi / c.budget) * 100) : 0;

  return (
    <div data-testid={`cantiere-row-${c.id}`} className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{c.nome}</span>
          <Badge variant="secondary" className="badge-tag">{c.stato?.replace("_", " ")}</Badge>
        </div>
        <div className="text-sm tabular-nums">
          <span className="text-red-600 font-semibold">{fmt(c.costi)}</span>
          <span className="text-muted-foreground"> / {fmt(c.budget)}</span>
        </div>
      </div>

      {/* Avanzamento LAVORI - calcolato dalle voci di computo */}
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Hammer className="h-3.5 w-3.5 text-primary" />
            <span className="font-semibold uppercase tracking-wider">Avanzamento lavori</span>
          </div>
          <span data-testid={`adv-pct-${c.id}`} className="font-bold tabular-nums">{pctLavori.toFixed(0)}%</span>
        </div>
        <Progress value={pctLavori} className="h-2 [&>div]:bg-primary" />
      </div>

      {/* Avanzamento PAGAMENTI */}
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Wallet className="h-3.5 w-3.5 text-emerald-600" />
            <span className="font-semibold uppercase tracking-wider">Pagamenti ricevuti</span>
          </div>
          <div className="flex items-center gap-2 tabular-nums">
            <span className="text-emerald-700 font-semibold">{fmt(c.fatture_incassate)}</span>
            <span className="text-muted-foreground">/</span>
            <span className="text-muted-foreground">{fmt(c.fatture_emesse)}</span>
            <span className="font-bold">({pctPag.toFixed(0)}%)</span>
          </div>
        </div>
        <Progress value={pctPag} className="h-2 [&>div]:bg-emerald-600" />
      </div>

      {/* Budget bar sottile */}
      <div>
        <div className="flex items-center justify-between text-[10px] mb-1 text-muted-foreground">
          <span className="uppercase tracking-wider">Spesa su budget</span>
          <span>{pctBudget.toFixed(0)}%</span>
        </div>
        <Progress value={pctBudget} className="h-1" />
      </div>
    </div>
  );
}
